import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import './SessionExpiredModal.css';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '420px',
    textAlign: 'center',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '16px'
}));

const IconCircle = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
}));

const StyledLockIcon = styled(LockOutlinedIcon)(({ theme }) => ({
  fontSize: '40px',
  color: '#D97706'
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontSize: '22px',
  fontWeight: 700,
  color: '#1E293B',
  paddingBottom: '8px',
  paddingTop: '0'
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: '0',
  paddingBottom: '24px'
}));

const LoginButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0B91D6 0%, #0369A1 100%)',
  color: '#fff',
  padding: '14px 40px',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(11, 145, 214, 0.4)',
  transition: 'all 0.3s ease',
  width: '100%',
  '&:hover': {
    background: 'linear-gradient(135deg, #0369A1 0%, #0B91D6 100%)',
    boxShadow: '0 6px 20px rgba(11, 145, 214, 0.5)',
    transform: 'translateY(-2px)'
  }
}));

/**
 * SessionExpiredModal - A reusable modal for displaying session expiration
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onLogin - Callback when login button is clicked
 * @param {string} title - Optional custom title
 * @param {string} message - Optional custom message
 */
const SessionExpiredModal = ({
  open,
  onClose,
  onLogin,
  title = 'Session Expired',
  message = 'Your session has expired for security reasons. Please log in again to continue using Visibeen.'
}) => {
  const handleLoginClick = () => {
    if (onLogin) {
      onLogin();
    } else {
      // Default behavior: redirect to login page
      window.location.href = '/login';
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="session-expired-dialog-title"
      aria-describedby="session-expired-dialog-description"
    >
      <IconWrapper>
        <IconCircle>
          <StyledLockIcon />
        </IconCircle>
      </IconWrapper>

      <StyledDialogTitle id="session-expired-dialog-title">
        {title}
      </StyledDialogTitle>

      <StyledDialogContent>
        <Typography
          id="session-expired-dialog-description"
          variant="body1"
          sx={{
            fontSize: '15px',
            lineHeight: 1.7,
            color: '#64748B',
            marginBottom: '8px'
          }}
        >
          {message}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: '#94A3B8',
            marginTop: '12px'
          }}
        >
          Don't worry, your data is safe. You just need to log in again.
        </Typography>
      </StyledDialogContent>

      <DialogActions sx={{ justifyContent: 'center', paddingBottom: '8px', paddingX: '24px' }}>
        <LoginButton
          onClick={handleLoginClick}
          startIcon={<LoginIcon />}
          variant="contained"
        >
          Log In Again
        </LoginButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default SessionExpiredModal;

